import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { IMovementScheduleComponent, IMovementScheduleFli } from '@modeso/types__aumico-lib-annualreport-be';
import { ViewMode } from '@modeso/types__aumico-lib-core-be';
import { IMovementScheduleEditableColumnHeaders } from '@modeso/types__aumico-lib-notes-be';
import Debug from 'debug';
import { cloneDeep, differenceBy, omit } from 'lodash-es';
import { MovementScheduleHelper } from '../../helpers/movementSchedule.helper';
import { IMovementScheduleComponentDelegation } from '../../interfaces/movement-schedule/IMovementScheduleComponentDelegation.interface';
import { IMovementScheduleDates } from '../../interfaces/movement-schedule/IMovementScheduleDates.interface';
import { IMovementScheduleTable } from '../../interfaces/movement-schedule/IMovementScheduleTable.interface';

import { IAnnualReportDefaultSettings } from '../../interfaces/IAnnualReportDefaultSettings.interface';
import { IMovementScheduleFormFli } from '../../interfaces/movement-schedule-form-fli.interface';
import { NotesPageBase } from '../NotesPageBase';


const debug = Debug('aumico:aumico-lib-notes-fe:MovementScheduleComponent');

@Component({
    selector: 'aumico-notes-movements-schedule',
    templateUrl: 'movement-schedule.component.html',
    styleUrls: ['movement-schedule.component.scss'],
})
export class MovementScheduleComponent extends NotesPageBase {
    movementScheduleForm: UntypedFormGroup | undefined;
    selectedFliFormArray: UntypedFormArray | undefined;

    readonly propertyOfMovementScheduleFliOptionsToDisplay = 'translatedCaption';

    editable = true;

    dates: IMovementScheduleDates;
    datesOfPreviousYear: IMovementScheduleDates = {} as IMovementScheduleDates;
    headersOfPreviousYear: IMovementScheduleEditableColumnHeaders;

    originalMovementScheduleComponentData: IMovementScheduleComponent;
    updatedMovementScheduleComponentData: IMovementScheduleComponent;

    previousSelectedMovementScheduleFlis: IMovementScheduleFli[] = [];
    previousMovementScheduleComponent: IMovementScheduleComponent | null;

    showFliDeletionConfirmationDialog = false;
    fliControlIndexToRemove: number | undefined;

    @Input() set data(value: IMovementScheduleComponent) {
        if (value != null) {
            this.originalMovementScheduleComponentData = cloneDeep<IMovementScheduleComponent>(value);
            this.updatedMovementScheduleComponentData = cloneDeep<IMovementScheduleComponent>(value);

            this.movementScheduleFlis = [
                ...this.movementScheduleHelper.addHeadersToMovementScheduleFlis(
                    this.originalMovementScheduleComponentData.headerOptions,
                    this.movementScheduleFlis,
                ),
            ];

            this.previousMovementScheduleComponent = cloneDeep<IMovementScheduleComponent>(
                value.previousMovementScheduleComponent,
            );

            this.setDataSource();

            this.preparePreviousYearData();
        }
    }

    @Input() set annualReportDefaultSettings(value: IAnnualReportDefaultSettings) {
        this.dates = {
            balanceCloseDate: value.currentBalanceDate,
            balanceOpenDate: value.previousBalanceDate,
            incomeCloseDate: value.currentIncomeDate,
            incomeOpenDate: value.previousIncomeDate,
        };

        // datesOfPreviousYear is derived from the previous-year Movement Schedule component itself
        // (see preparePreviousYearData), so the previous table no longer depends on the autofill
        // annual report being loaded in the store, which it is not after a plain reload.
    }

    private movementScheduleFlis: IMovementScheduleFli[] = [];

    @Input() set flis(value: IMovementScheduleFli[]) {
        this.movementScheduleFlis = cloneDeep<IMovementScheduleFli[]>(value);

        if (this.originalMovementScheduleComponentData != null) {
            this.movementScheduleFlis = [
                ...this.movementScheduleHelper.addHeadersToMovementScheduleFlis(
                    this.originalMovementScheduleComponentData.headerOptions,
                    this.movementScheduleFlis,
                ),
            ];

            this.setDataSource();
        }
    }

    @Input() reportLocale: string;
    @Input() defaultLanguage = 'de-ch';

    @Input() componentIndex: number;
    @Input() viewMode = ViewMode.PREVIEW;

    outputDelegation: IMovementScheduleComponentDelegation;
    @Input() public set delegate(value: IMovementScheduleComponentDelegation) {
        if (value != null) {
            this.outputDelegation = value;
        }
    }

    @Input() set resetValuesFromMappingTriggered(value: boolean) {
        if (!value) {
            return;
        }
        this.resetFormValuesFromMapping();
    }

    @Input() highlightTodos = false;

    @Input() tableNumbers: string[] = [];

    emptyFLIselector: number;

    selectedMovementsScheduleFlis: Map<number, IMovementScheduleFormFli> = new Map<number, IMovementScheduleFormFli>();
    availableMovementsScheduleFlis: Map<number, IMovementScheduleFormFli[]> = new Map<
        number,
        IMovementScheduleFormFli[]
    >();

    getAvailableMovementsScheduleFlis(index: number): IMovementScheduleFormFli[] {
        return this.availableMovementsScheduleFlis.get(index);
    }
    constructor(
        private formBuilder: UntypedFormBuilder,
        private movementScheduleHelper: MovementScheduleHelper,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private readonly changeDetector: ChangeDetectorRef,
    ) {
        super();
        this.initializeForm();
        this.addSvgIcon('aumico-pattern-icon-remove');
        this.addSvgIcon('aumico-pattern-icon-edit-write');
    }

    initializeForm() {
        this.movementScheduleForm = this.formBuilder.group({
            selectedFlis: this.formBuilder.array([]),
        });

        this.movementScheduleForm.setControl('selectedFlis', this.getAllAsFormArray());
    }

    private setDataSource() {
        if (this.originalMovementScheduleComponentData.flis.length > 0 && this.movementScheduleFlis.length > 0) {
            this.movementScheduleForm.setControl('selectedFlis', this.getAllAsFormArray());

            // prepare the selected flis
            this.prepareSelectedMovementScheduleFlis();

            // prepare available flis selections
            this.prepareAvailableMovementSchduleFliSelections();
        } else {
            const translatedMovementScheduleFlis: IMovementScheduleFormFli[] = cloneDeep<IMovementScheduleFormFli[]>(
                this.movementScheduleHelper.addTranslatedCaptionToMovementScheduleFlis(
                    this.movementScheduleFlis,
                    this.reportLocale,
                    this.defaultLanguage,
                ),
            );
            this.availableMovementsScheduleFlis.set(0, translatedMovementScheduleFlis);
        }
    }

    private addFormGroup(dataSource?: IMovementScheduleFormFli): UntypedFormGroup {
        const dataSourceFormGroup = {};
        const value = dataSource == null ? this.movementScheduleHelper.createEmptyMovementScheduleFli() : dataSource;
        dataSourceFormGroup['fli'] = new UntypedFormControl(value, Validators.required);
        return new UntypedFormGroup(dataSourceFormGroup);
    }

    private getAllAsFormArray(): UntypedFormArray {
        const movementScheduleFlis =
            this.originalMovementScheduleComponentData != null &&
                this.originalMovementScheduleComponentData.flis.length > 0
                ? this.originalMovementScheduleComponentData.flis
                : [this.movementScheduleHelper.createEmptyMovementScheduleFli()];

        const translatedMovementScheduleFlis: IMovementScheduleFormFli[] = cloneDeep<IMovementScheduleFormFli[]>(
            this.movementScheduleHelper.addTranslatedCaptionToMovementScheduleFlis(
                movementScheduleFlis,
                this.reportLocale,
                this.defaultLanguage,
            ),
        );

        const formGroups = translatedMovementScheduleFlis.map(this.addFormGroup);
        return new UntypedFormArray(formGroups);
    }

    private prepareSelectedMovementScheduleFlis() {
        const translatedMovementScheduleFlis: IMovementScheduleFormFli[] =
            this.movementScheduleHelper.addTranslatedCaptionToMovementScheduleFlis(
                this.updatedMovementScheduleComponentData.flis,
                this.reportLocale,
                this.defaultLanguage,
            );

        translatedMovementScheduleFlis.forEach((fli, index) => {
            this.selectedMovementsScheduleFlis.set(index, fli);
        });
    }

    private prepareAvailableMovementSchduleFliSelections() {
        const selectedMovementScheduleFli = this.movementScheduleHelper.getMovementScheduleFlisFromFormArray(
            this.movementScheduleForm.get('selectedFlis').value,
        );
        selectedMovementScheduleFli.forEach((fli, index) => {
            const temporaryCopyOfMovementScheduleFlis = [...this.movementScheduleFlis];

            // remove all selected flis except the current one it should be pushed, then prioritize
            const differenceOfFlis = differenceBy(
                temporaryCopyOfMovementScheduleFlis,
                selectedMovementScheduleFli,
                'id',
            );

            // check the current fli is not an empty or a mocked one
            if (fli != null && fli.id !== '' && !isNaN(fli.position)) {
                differenceOfFlis.push(fli);
            }

            const translatedMovementScheduleFlis: IMovementScheduleFormFli[] =
                this.movementScheduleHelper.addTranslatedCaptionToMovementScheduleFlis(
                    differenceOfFlis,
                    this.reportLocale,
                    this.defaultLanguage,
                );

            this.availableMovementsScheduleFlis.set(
                index,
                this.movementScheduleHelper.prioritizeFlis(translatedMovementScheduleFlis),
            );
        });
    }

    onSelectionChanged(selectedFli: IMovementScheduleFormFli, formControlIndex: number) {
        this.emptyFLIselector = undefined;

        this.selectedMovementsScheduleFlis.set(formControlIndex, { ...selectedFli });

        this.prepareAvailableMovementSchduleFliSelections();

        const selectedFliWithoutTranslatedCaption: IMovementScheduleFli = cloneDeep(
            omit(selectedFli, ['translatedCaption']),
        );

        this.handlePreviousYearTableFliSelection(selectedFliWithoutTranslatedCaption, formControlIndex);

        this.updatedMovementScheduleComponentData.flis[formControlIndex] = cloneDeep<IMovementScheduleFli>(
            selectedFliWithoutTranslatedCaption,
        );

        this.setNumberingToFli(true, formControlIndex);

        // Make sure the form control is updated
        const selectedFliFormArray = this.movementScheduleForm.get('selectedFlis') as UntypedFormArray;
        const formGroup = selectedFliFormArray.at(formControlIndex) as UntypedFormGroup;
        formGroup.get('fli').setValue(selectedFli, { emitEvent: false });

        // emit
        this.emitChanges();
    }

    addItem(index: number): void {
        // Insert a new empty row AFTER the given index.
        const selectedFliFormArray = this.movementScheduleForm.get('selectedFlis') as UntypedFormArray;

        // 1) If a single empty row exists, remove it and adjust the target index
        const emptyIdx = selectedFliFormArray.controls.findIndex((fg) => {
            const val = fg.get('fli').value;
            return val != null && val.id === '';
        });

        if (emptyIdx !== -1) {
            selectedFliFormArray.removeAt(emptyIdx);
            this.updatedMovementScheduleComponentData.flis.splice(emptyIdx, 1);
            if (this.updatedMovementScheduleComponentData.footnotes) {
                this.updatedMovementScheduleComponentData.footnotes.splice(emptyIdx, 1);
            }
            if (emptyIdx < this.previousSelectedMovementScheduleFlis.length) {
                this.previousSelectedMovementScheduleFlis.splice(emptyIdx, 1);
            }

            if (emptyIdx <= index) {
                index = index - 1;
            }
        }

        // 2) Compute insert position (after current index); fallback to append if index is invalid
        const maxIndex = selectedFliFormArray.length - 1;
        const insertAt =
            typeof index === 'number' && index >= 0 && index <= maxIndex ? index + 1 : selectedFliFormArray.length;

        // Store index where empty FLI selector is
        this.emptyFLIselector = insertAt;

        // 3) Insert into FormArray and sync backing arrays
        selectedFliFormArray.insert(insertAt, this.addFormGroup());
        this.updatedMovementScheduleComponentData.flis.splice(
            insertAt,
            0,
            this.movementScheduleHelper.createEmptyMovementScheduleFli(),
        );

        if (this.updatedMovementScheduleComponentData.footnotes) {
            this.updatedMovementScheduleComponentData.footnotes.splice(insertAt, 0, '');
        }

        if (this.previousMovementScheduleComponent != null) {
            (this.previousSelectedMovementScheduleFlis as unknown as Array<IMovementScheduleFli | undefined>).splice(
                insertAt,
                0,
                undefined,
            );
        }

        // 4) Rebuild maps so template bindings use correct indices after the insertion
        this.prepareSelectedMovementScheduleFlis();
        this.prepareAvailableMovementSchduleFliSelections();
    }

    onRemoveSelection(index: number) {
        const selectedFli = this.selectedMovementsScheduleFlis.get(index);
        if (selectedFli != null) {
            // need to reselect fli again because hamSimpleDropDown already emptied internally the selection
            this.onSelectionChanged(selectedFli, index);
        }

        this.openFliDeletionConfirmationDialog(index);
    }

    private handlePreviousYearTableFliSelection(selectedFli: IMovementScheduleFli, index: number) {
        if (this.previousMovementScheduleComponent != null) {
            const autofillOfFli = this.previousMovementScheduleComponent.flis.find((fli) => fli.id === selectedFli.id);
            this.previousSelectedMovementScheduleFlis[index] = autofillOfFli;
        }
    }

    openFliDeletionConfirmationDialog(formControlIndex: number): void {
        this.showFliDeletionConfirmationDialog = true;
        this.fliControlIndexToRemove = formControlIndex;
    }

    onCancelFliDeletionConfirmationDialog(): void {
        this.showFliDeletionConfirmationDialog = false;
        this.fliControlIndexToRemove = undefined;
    }

    remove() {
        const selectedFliFormArray = this.movementScheduleForm.get('selectedFlis') as UntypedFormArray;
        selectedFliFormArray.removeAt(this.fliControlIndexToRemove);
        this.updatedMovementScheduleComponentData.flis.splice(this.fliControlIndexToRemove, 1);

        // Keep footnotes aligned: remove the footnote at the same index if present
        if (
            this.updatedMovementScheduleComponentData.footnotes &&
            this.updatedMovementScheduleComponentData.footnotes.length > this.fliControlIndexToRemove
        ) {
            this.updatedMovementScheduleComponentData.footnotes.splice(this.fliControlIndexToRemove, 1);
        }

        // Instead of just splicing by index, rebuild previous year data to match current FLIs by ID
        this.updatePreviousYearDataForCurrentSelection();

        // // emit
        this.emitChanges();

        // prepare the selected flis
        this.prepareSelectedMovementScheduleFlis();

        this.prepareAvailableMovementSchduleFliSelections();

        this.showFliDeletionConfirmationDialog = false;
        this.fliControlIndexToRemove = undefined;
    }

    private preparePreviousYearData(): void {
        if (this.previousMovementScheduleComponent == null) {
            return;
        }

        // Use the previous-year Movement Schedule's own stored dates so the previous table no longer
        // depends on the autofill annual report being present in the store (it is absent after a plain
        // reload). These are persisted as clean UTC-midnight ISO strings, so the existing date display
        // renders the correct calendar day with no timezone handling.
        this.datesOfPreviousYear = {
            balanceCloseDate: this.previousMovementScheduleComponent.currentBalanceYearDate,
            balanceOpenDate: this.previousMovementScheduleComponent.previousBalanceYearDate,
            incomeCloseDate: this.previousMovementScheduleComponent.currentIncomeYearDate,
            incomeOpenDate: this.previousMovementScheduleComponent.previousIncomeYearDate,
        };

        this.headersOfPreviousYear = cloneDeep<IMovementScheduleEditableColumnHeaders>(
            this.previousMovementScheduleComponent.headerOptions,
        );
        // ordering the previous flis correctly according to the order of the current selection
        this.updatedMovementScheduleComponentData.flis.forEach((selectedFli) => {
            const correspondingPreviousSelectedFli = this.previousMovementScheduleComponent.flis.find(
                (fli) => fli.id === selectedFli.id,
            );
            this.previousSelectedMovementScheduleFlis.push(correspondingPreviousSelectedFli);
        });
    }

    updateSelectedMovementScheduleFliData(updatedData: IMovementScheduleTable, formControlIndex: number) {
        this.updatedMovementScheduleComponentData.flis[formControlIndex].accounts = updatedData.dataSource;
        this.updatedMovementScheduleComponentData.flis[formControlIndex].totalRow = updatedData.totalRow;
        this.updateHeaders(updatedData.headers, formControlIndex);

        // Handle footnotes - store them at the component level
        if (updatedData.footnotes && updatedData.footnotes.length > 0) {
            // Initialize footnotes array if it doesn't exist
            if (!this.updatedMovementScheduleComponentData.footnotes) {
                this.updatedMovementScheduleComponentData.footnotes = [];
            }
            // Store footnotes for this specific FLI index
            this.updatedMovementScheduleComponentData.footnotes[formControlIndex] = updatedData.footnotes[0];
        } else {
            // Clear footnote for this FLI if no footnotes provided
            if (this.updatedMovementScheduleComponentData.footnotes) {
                this.updatedMovementScheduleComponentData.footnotes[formControlIndex] = '';
            }
        }

        this.emitChanges();
    }

    updatePreviousMovementScheduleFliData(updatedData: IMovementScheduleTable, formControlIndex: number): void {
        if (updatedData.dataSource.length === 0) {
            return;
        }

        // Safety check: ensure we have valid previous year data and the index exists
        if (!this.updatedMovementScheduleComponentData.previousMovementScheduleComponent?.flis ||
            !this.previousMovementScheduleComponent?.flis ||
            formControlIndex >= this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.flis.length ||
            formControlIndex >= this.previousMovementScheduleComponent.flis.length ||
            formControlIndex < 0) {
            return;
        }

        // Get the current FLI ID to ensure we're updating the correct previous year FLI
        const currentFliId = this.updatedMovementScheduleComponentData.flis[formControlIndex]?.id;
        if (!currentFliId) {
            return;
        }

        // Find the corresponding previous year FLI by ID instead of using index
        const previousFliIndex = this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.flis
            .findIndex(fli => fli.id === currentFliId);

        if (previousFliIndex === -1) {
            return;
        }

        // Update using the found index instead of the passed formControlIndex
        this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.flis[previousFliIndex].accounts =
            updatedData.dataSource;
        this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.flis[previousFliIndex].totalRow =
            updatedData.totalRow;

        // Update headers for previous year component using the correct index
        this.updatePreviousHeaders(updatedData.headers, previousFliIndex);

        // Also update the local reference if it exists at the correct index
        const localPreviousFliIndex = this.previousMovementScheduleComponent.flis
            .findIndex(fli => fli.id === currentFliId);

        if (localPreviousFliIndex !== -1) {
            this.previousMovementScheduleComponent.flis[localPreviousFliIndex].accounts = updatedData.dataSource;
            this.previousMovementScheduleComponent.flis[localPreviousFliIndex].totalRow = updatedData.totalRow;
        }

        // Handle footnotes - store them at the component level using the correct index
        if (updatedData.footnotes && updatedData.footnotes.length > 0) {
            // Initialize footnotes array if it doesn't exist
            if (!this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.footnotes) {
                this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.footnotes = [];
            }
            // Ensure footnotes array is large enough
            while (this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.footnotes.length <= previousFliIndex) {
                this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.footnotes.push('');
            }
            // Store footnotes for the correct FLI index
            this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.footnotes[previousFliIndex] =
                updatedData.footnotes[0];
        } else {
            // Clear footnote for this FLI if no footnotes provided
            if (this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.footnotes &&
                this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.footnotes.length > previousFliIndex) {
                this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.footnotes[previousFliIndex] = '';
            }
        }

        this.emitChanges();
    }

    private updateHeaders(headers: IMovementScheduleEditableColumnHeaders, formControlIndex: number): void {
        const { column1, column2, column3, column4 } = headers;
        if (this.updatedMovementScheduleComponentData.flis[formControlIndex].headers != null) {
            this.updatedMovementScheduleComponentData.flis[formControlIndex].headers.column1 = column1;
            this.updatedMovementScheduleComponentData.flis[formControlIndex].headers.column2 = column2;
            this.updatedMovementScheduleComponentData.flis[formControlIndex].headers.column3 = column3;
            this.updatedMovementScheduleComponentData.flis[formControlIndex].headers.column4 = column4;
        }
    }

    private updatePreviousHeaders(headers: IMovementScheduleEditableColumnHeaders, formControlIndex: number): void {
        const { column1, column2, column3, column4 } = headers;

        // Safety check for updatedMovementScheduleComponentData
        if (this.updatedMovementScheduleComponentData.previousMovementScheduleComponent?.flis &&
            formControlIndex >= 0 &&
            formControlIndex < this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.flis.length &&
            this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.flis[formControlIndex].headers != null) {
            this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.flis[formControlIndex].headers.column1 = column1;
            this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.flis[formControlIndex].headers.column2 = column2;
            this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.flis[formControlIndex].headers.column3 = column3;
            this.updatedMovementScheduleComponentData.previousMovementScheduleComponent.flis[formControlIndex].headers.column4 = column4;
        }

        // Safety check for local previous component reference
        if (this.previousMovementScheduleComponent?.flis &&
            formControlIndex >= 0 &&
            formControlIndex < this.previousMovementScheduleComponent.flis.length &&
            this.previousMovementScheduleComponent.flis[formControlIndex].headers != null) {
            this.previousMovementScheduleComponent.flis[formControlIndex].headers.column1 = column1;
            this.previousMovementScheduleComponent.flis[formControlIndex].headers.column2 = column2;
            this.previousMovementScheduleComponent.flis[formControlIndex].headers.column3 = column3;
            this.previousMovementScheduleComponent.flis[formControlIndex].headers.column4 = column4;
        }
    }

    resetFormValuesFromMapping(): void {
        const selectedFlis = this.movementScheduleForm.get('selectedFlis').value;
        const currentlySelectedFliIds = selectedFlis
            .map((item: any) => item.fli?.id)
            .filter((id: string) => id && id !== '');

        // Get fresh FLI data from mapping (this contains new accounts)
        const translatedMovementScheduleFlis: IMovementScheduleFormFli[] =
            this.movementScheduleHelper.addTranslatedCaptionToMovementScheduleFlis(
                this.movementScheduleFlis,
                this.reportLocale,
                this.defaultLanguage,
            );

        // Rebuild the component data and form with fresh mapping data
        const updatedFlis: IMovementScheduleFli[] = [];
        const newFormControls: any[] = [];

        // For each currently selected FLI ID, get the fresh data from mapping and merge with existing user data
        currentlySelectedFliIds.forEach((fliId: string) => {
            const freshFliData = translatedMovementScheduleFlis.find((fli) => fli.id === fliId);
            const existingFliData = this.updatedMovementScheduleComponentData.flis.find((fli) => fli.id === fliId);

            if (freshFliData) {
                // Start with fresh FLI data (structure, new accounts, etc.)
                const fliWithoutTranslatedCaption: IMovementScheduleFli = cloneDeep(
                    omit(freshFliData, ['translatedCaption']),
                );

                // If we have existing user data for this FLI, merge it smartly
                if (existingFliData) {

                    // Preserve headers user data if exists
                    if (existingFliData.headers && fliWithoutTranslatedCaption.headers) {
                        fliWithoutTranslatedCaption.headers.column1 = existingFliData.headers.column1;
                        fliWithoutTranslatedCaption.headers.column2 = existingFliData.headers.column2;
                        fliWithoutTranslatedCaption.headers.column3 = existingFliData.headers.column3;
                        fliWithoutTranslatedCaption.headers.column4 = existingFliData.headers.column4;
                    }

                    // Smart merge accounts based on ID matching
                    if (fliWithoutTranslatedCaption.accounts && existingFliData.accounts) {
                        const existingAccountsMap = new Map(
                            existingFliData.accounts.map(acc => [acc.id, acc])
                        );

                        fliWithoutTranslatedCaption.accounts = fliWithoutTranslatedCaption.accounts.map(freshAccount => {
                            const existingAccount = existingAccountsMap.get(freshAccount.id);

                            if (existingAccount) {
                                // Merge: keep user columns, update mapping data
                                return {
                                    ...freshAccount, // Fresh data (text, closeValue, openValue, id)
                                    column1: existingAccount.column1, // Preserve user data
                                    column2: existingAccount.column2,
                                    column3: existingAccount.column3,
                                    column4: existingAccount.column4,
                                };
                            } else {
                                return freshAccount;
                            }
                        });

                    }

                    // Keep totalRow flag if it existed (will be recalculated later)
                    if (existingFliData.totalRow) {
                        fliWithoutTranslatedCaption.totalRow = existingFliData.totalRow;
                    }


                }

                updatedFlis.push(fliWithoutTranslatedCaption);

                // Create merged FLI with translatedCaption for form control
                const mergedFliWithTranslation = {
                    ...fliWithoutTranslatedCaption,
                    translatedCaption: freshFliData.translatedCaption
                };

                // Use the merged data WITH translatedCaption for form controls so dropdown can match it
                newFormControls.push({ fli: mergedFliWithTranslation });
            }
        });

        // Update component data with fresh FLI data
        this.updatedMovementScheduleComponentData.flis = updatedFlis;

        // Initialize footnotes array to match the new FLIs
        if (!this.updatedMovementScheduleComponentData.footnotes) {
            this.updatedMovementScheduleComponentData.footnotes = [];
        }
        // Ensure footnotes array has the right length
        while (this.updatedMovementScheduleComponentData.footnotes.length < updatedFlis.length) {
            this.updatedMovementScheduleComponentData.footnotes.push('');
        }
        // Trim footnotes if too long
        if (this.updatedMovementScheduleComponentData.footnotes.length > updatedFlis.length) {
            this.updatedMovementScheduleComponentData.footnotes =
                this.updatedMovementScheduleComponentData.footnotes.slice(0, updatedFlis.length);
        }

        // Rebuild form array with fresh data
        const selectedFliFormArray = this.movementScheduleForm.get('selectedFlis') as UntypedFormArray;
        selectedFliFormArray.clear(); // Clear old form controls

        // Add form controls for the updated FLIs
        newFormControls.forEach((controlData) => {
            selectedFliFormArray.push(this.addFormGroup(controlData.fli));
        });

        // If no FLIs were preserved, add an empty one
        if (newFormControls.length === 0) {
            selectedFliFormArray.push(this.addFormGroup());
            this.updatedMovementScheduleComponentData.flis = [
                this.movementScheduleHelper.createEmptyMovementScheduleFli(),
            ];
        }

        // Recalculate total rows for all FLIs after the merge
        this.updatedMovementScheduleComponentData.flis.forEach((fli, index) => {
            if (fli.totalRow && fli.accounts && fli.accounts.length > 0) {
                // Recalculate total row values from accounts
                const totalCloseValue = fli.accounts.reduce((sum, account) => sum + (account.closeValue || 0), 0);
                const totalOpenValue = fli.accounts.reduce((sum, account) => sum + (account.openValue || 0), 0);
                const totalColumn1 = fli.accounts.reduce((sum, account) => sum + (account.column1 || 0), 0);
                const totalColumn2 = fli.accounts.reduce((sum, account) => sum + (account.column2 || 0), 0);
                const totalColumn3 = fli.accounts.reduce((sum, account) => sum + (account.column3 || 0), 0);
                const totalColumn4 = fli.accounts.reduce((sum, account) => sum + (account.column4 || 0), 0);

                // Update total row with recalculated values
                fli.totalRow = {
                    ...fli.totalRow,
                    closeValue: totalCloseValue,
                    openValue: totalOpenValue,
                    column1: totalColumn1,
                    column2: totalColumn2,
                    column3: totalColumn3,
                    column4: totalColumn4,
                };
            }
        });

        // Update previous year data to match current selection
        this.updatePreviousYearDataForCurrentSelection();

        // prepare the selected flis
        this.prepareSelectedMovementScheduleFlis();

        // prepare available flis selections
        this.prepareAvailableMovementSchduleFliSelections();

        // Emit changes to notify parent component
        this.emitChanges();
    }

    private updatePreviousYearDataForCurrentSelection(): void {
        if (this.previousMovementScheduleComponent == null) {
            return;
        }

        // Rebuild previous year data to match current FLI selection
        this.previousSelectedMovementScheduleFlis = [];
        this.updatedMovementScheduleComponentData.flis.forEach((selectedFli) => {
            const correspondingPreviousSelectedFli = this.previousMovementScheduleComponent.flis.find(
                (fli) => fli.id === selectedFli.id,
            );
            this.previousSelectedMovementScheduleFlis.push(correspondingPreviousSelectedFli);
        });
    }

    private emitChanges() {
        if (this.outputDelegation != null) {
            this.outputDelegation.getMovementScheduleComponentData(
                this.updatedMovementScheduleComponentData,
                this.componentIndex,
            );
        }
    }

    private addSvgIcon(name: string) {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    setNumberingToFli(addNumbering: boolean, index: number): void {
        this.updatedMovementScheduleComponentData.flis[index].addNumbering = addNumbering;
    }

    getTableNumberIndex(selectedFliControlIndex: number) {
        return selectedFliControlIndex > this.emptyFLIselector ? selectedFliControlIndex - 1 : selectedFliControlIndex;
    }
}
